import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getIdClaims,
  getStoredTokens,
  login as doLogin,
  logout as doLogout,
} from "./cognito";

interface AuthState {
  isAuthenticated: boolean;
  email?: string;
  userId?: string;
  isAdmin: boolean;
  login: (idp?: "Google") => void;
  logout: () => void;
  /** Called by the callback route after a successful token exchange. */
  refreshFromStorage: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tick, setTick] = useState(0);
  const refreshFromStorage = useCallback(() => setTick((t) => t + 1), []);

  // Re-read auth state across tabs.
  useEffect(() => {
    const onStorage = () => refreshFromStorage();
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshFromStorage]);

  const value = useMemo<AuthState>(() => {
    void tick; // recompute when tick changes
    const tokens = getStoredTokens();
    const claims = getIdClaims();
    const groups = claims?.["cognito:groups"] ?? [];
    return {
      isAuthenticated: Boolean(tokens?.accessToken),
      email: claims?.email,
      userId: claims?.sub,
      isAdmin: Array.isArray(groups) && groups.includes("admin"),
      login: (idp) => void doLogin(idp),
      logout: () => doLogout(),
      refreshFromStorage,
    };
  }, [tick, refreshFromStorage]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
